import { Ban, Check } from 'lucide-react';
import { observer } from 'mobx-react';
import React from 'react';
import { Link } from 'react-router-dom';
import { useWrapRequest } from 'use-wrap-request';
import { userSearchDebounce } from '../components/hooks/use-search';
import { useSearchReducer } from '../components/hooks/useSearchReducer';
import { PageHeader } from '../components/page-header';
import { CellProps, TableComponent } from '../components/table';
import { Button } from '../components/ui/button';
import { assertDefined } from '../components/utils/error';
import { Api } from '../core/api';
import { ActivitySourceDto } from '../core/api/FitnessActivityClient';
import { links } from '../core/router';
import { injectTSDI } from '../core/tsdi';

const AllowedIcon = ({ allowed }: { allowed?: boolean }) =>
    allowed ? (
        <Check className="size-4 text-success" />
    ) : (
        <Ban className="size-4 text-destructive" />
    );

export default observer(() => {
    const { fitnessActivityClient } = injectTSDI(Api);

    const [searchParams, dispatch] = React.useReducer(useSearchReducer, {
        size: 30,
        page: 0,
        sort: '-createdDate'
    });

    const [initialSearch] = React.useState<string | undefined>();

    const { setSearchTerm } = userSearchDebounce(dispatch);

    const activitySourcesRequest = useWrapRequest(
        async (searchParams) => {
            const result =
                await fitnessActivityClient.admin.activitySourceAdminControllerGetActivitySources(
                    searchParams
                );

            return {
                ...result.data,
                content: result.data.content || []
            };
        },
        {
            defaultData: {
                content: [],
                number: 0,
                size: 0,
                totalPages: 0,
                totalElements: 0
            },
            deps: [searchParams]
        }
    );

    const updateActivitySourceRequest = useWrapRequest(
        async (source: ActivitySourceDto) => {
            assertDefined(source.id);
            const result =
                await fitnessActivityClient.admin.activitySourceAdminControllerUpdateActivitySource(
                    source.id,
                    source
                );

            return result.data;
        },
        { successMessage: () => 'Source was changed' }
    );

    const onToggleSteps = async (source: ActivitySourceDto) => {
        try {
            source.stepsAllowed = !source.stepsAllowed;
            await updateActivitySourceRequest.request([source]);
        } catch (err) {
            alert(err);
        }

        activitySourcesRequest.request();
    };

    const onToggleCycling = async (source: ActivitySourceDto) => {
        try {
            source.workoutsAllowed = !source.workoutsAllowed;
            await updateActivitySourceRequest.request([source]);
        } catch (err) {
            alert(err);
        }
        activitySourcesRequest.request();
    };

    return (
        <div className="mx-auto w-full max-w-[900px]">
            <PageHeader title="Activity sources" />

            <TableComponent<ActivitySourceDto[]>
                emptyText="No entries"
                items={activitySourcesRequest}
                columns={[
                    {
                        Header: 'Source package',
                        accessor: 'applicationPackage',
                        disableSortBy: true,
                        style: {
                            width: 300
                        }
                    },
                    {
                        Header: 'Steps',
                        disableSortBy: true,
                        Cell: ({
                            row: { original: row }
                        }: CellProps<ActivitySourceDto>) => (
                            <AllowedIcon allowed={row.stepsAllowed} />
                        )
                    },
                    {
                        Header: 'Cycling',
                        disableSortBy: true,
                        Cell: ({
                            row: { original: row }
                        }: CellProps<ActivitySourceDto>) => (
                            <AllowedIcon allowed={row.workoutsAllowed} />
                        )
                    },
                    {
                        Header: 'User',
                        Cell: ({
                            row: { original: row }
                        }: CellProps<ActivitySourceDto>) => {
                            const { origin } = row;

                            if (!origin?.userId || !origin.tenantId) {
                                return null;
                            }

                            return (
                                <Link
                                    className="text-primary hover:underline"
                                    to={links.tenantEmployeeDetails(
                                        origin.tenantId,
                                        origin.userId.toString()
                                    )}
                                >
                                    {origin.userId}
                                </Link>
                            );
                        }
                    },
                    {
                        Header: 'Actions',
                        Cell: ({
                            row: { original: row }
                        }: CellProps<ActivitySourceDto>) => (
                            <div className="flex flex-col items-start gap-1">
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="h-auto p-0"
                                    onClick={() => onToggleSteps(row)}
                                >
                                    {row.stepsAllowed
                                        ? 'Reject steps'
                                        : 'Approve steps'}
                                </Button>
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="h-auto p-0"
                                    onClick={() => onToggleCycling(row)}
                                >
                                    {row.workoutsAllowed
                                        ? 'Reject cycling'
                                        : 'Approve cycling'}
                                </Button>
                            </div>
                        )
                    }
                ]}
                onPageSizeChange={(size) => dispatch({ type: 'size', size })}
                onChangePage={(page) => void dispatch({ type: 'page', page })}
                onSearchChange={(val) => void setSearchTerm(val)}
                initialSearch={initialSearch}
                key={initialSearch}
            />
        </div>
    );
});
